import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useActionSets,
  useCreateActionSet,
  useCreateMatchSet,
  useDeleteActionSet,
  useDeleteMatchSet,
  useMatchSets,
  useUpdateActionSet,
  useUpdateMatchSet,
} from "../../../api/queries";
import { ApiError, errorText } from "../../../api/common";
import type {
  ActionSetDto,
  ActionSetId,
  MatchSetDto,
  MatchSetId,
} from "../../../api/types";
import { AdminListRow } from "../../../components/AdminList";
import { CenteredSpinner } from "../../../components/CenteredSpinner";
import { useRuleSetNames } from "../../../hooks/useRuleSetNames";
import {
  summarizeActions,
  summarizeCondition,
} from "../../../logic/auto-transaction-summary";
import { describeProblems } from "../../../logic/rule-problems";
import { LibrarySetForm } from "./LibrarySetForm";
import { UsedByChip } from "./UsedByChip";

/**
 * The rule library: named conditions ("match sets") and named action lists
 * ("action sets") that rules cite by id instead of copying.
 *
 * Deleting an entry that anything still cites is refused by the backend, which
 * returns the full usage; that message is what the error alert shows, so the
 * user learns *what* is in the way rather than just that something is.
 */
/**
 * The backend refuses to delete an entry anything still cites, and returns the
 * full usage with the error. Name what is in the way — "in use" alone leaves
 * the user to go hunting for the citing rules themselves.
 */
const describeDeleteFailure = (err: unknown): string => {
  const data = err instanceof ApiError ? err.data : undefined;
  if (data?.type !== "libraryEntryInUse") return errorText(err);

  const cited = [
    ...data.usage.rules.map((r) => r.name),
    ...data.usage.matchSets.map((s) => s.name),
    ...data.usage.actionSets.map((s) => s.name),
  ];
  return `Still in use by ${cited.join(", ")}. Remove the reference there first.`;
};

export const LibraryPage = () => {
  const [tab, setTab] = useState<"match" | "action">("match");
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<
    { kind: "match" | "action"; set?: MatchSetDto | ActionSetDto } | undefined
  >();

  const matchSets = useMatchSets();
  const actionSets = useActionSets();
  const resolvers = useRuleSetNames();

  const createMatch = useCreateMatchSet();
  const updateMatch = useUpdateMatchSet();
  const deleteMatch = useDeleteMatchSet();
  const createAction = useCreateActionSet();
  const updateAction = useUpdateActionSet();
  const deleteAction = useDeleteActionSet();

  const loading = matchSets.isLoading || actionSets.isLoading;

  const entries = useMemo(
    () => (tab === "match" ? (matchSets.data ?? []) : (actionSets.data ?? [])),
    [tab, matchSets.data, actionSets.data],
  );

  /** Open a cited entry's editor, from another entry's used-by list. */
  const openEntry = (
    kind: "match" | "action",
    id: MatchSetId | ActionSetId,
  ) => {
    const sets: (MatchSetDto | ActionSetDto)[] =
      (kind === "match" ? matchSets.data : actionSets.data) ?? [];
    const set = sets.find((s) => s.id === id);
    if (!set) return;
    setTab(kind);
    setEditing({ kind, set });
  };

  const handleDelete = async (set: MatchSetDto | ActionSetDto) => {
    setError(null);
    try {
      if (tab === "match") {
        await deleteMatch.mutateAsync(set.id);
      } else {
        await deleteAction.mutateAsync(set.id);
      }
    } catch (err) {
      setError(describeDeleteFailure(err));
    }
  };

  const handleSave = async (values: {
    name: string;
    description: string | null;
    condition: MatchSetDto["condition"];
    inputs: ActionSetDto["inputs"];
    actions: ActionSetDto["actions"];
  }) => {
    const { name, description, condition, inputs, actions } = values;
    const existing = editing?.set;
    if (editing?.kind === "match") {
      const body = { name, description, condition };
      return existing
        ? updateMatch.mutateAsync({ id: existing.id, set: body })
        : createMatch.mutateAsync(body);
    }
    const body = { name, description, inputs, actions };
    return existing
      ? updateAction.mutateAsync({ id: existing.id, set: body })
      : createAction.mutateAsync(body);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
      >
        <Typography variant="h5">Library</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ kind: tab })}
        >
          New
        </Button>
      </Stack>

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Shared pieces a rule can cite instead of copying. Editing one here
        changes every rule that uses it.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next as "match" | "action")}
        sx={{ mb: 1 }}
      >
        <Tab
          value="match"
          label={`Match sets (${matchSets.data?.length ?? 0})`}
        />
        <Tab
          value="action"
          label={`Action sets (${actionSets.data?.length ?? 0})`}
        />
      </Tabs>

      {loading ? (
        <CenteredSpinner />
      ) : entries.length === 0 ? (
        <Typography sx={{ color: "text.secondary", p: 2 }}>
          {tab === "match"
            ? "No match sets yet. A match set holds a condition — an “only unclassified” guard, say — that many rules can share."
            : "No action sets yet. An action set holds the actions many rules have in common, such as the group, expense type and categories every subscription gets."}
        </Typography>
      ) : (
        <List>
          {entries.map((set) => {
            const summary =
              "condition" in set
                ? summarizeCondition(set.condition, resolvers)
                : summarizeActions(set.actions, resolvers);
            return (
              <AdminListRow
                key={set.id}
                avatar={
                  tab === "match" ? <FilterAltIcon /> : <PlaylistAddCheckIcon />
                }
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <span>{set.name}</span>
                    <UsedByChip
                      usage={set.usedBy}
                      onOpenMatchSet={(id) => openEntry("match", id)}
                      onOpenActionSet={(id) => openEntry("action", id)}
                    />
                    {set.problems.length > 0 && (
                      <Tooltip title={describeProblems(set.problems)}>
                        <Chip
                          size="small"
                          color="error"
                          icon={<WarningAmberIcon />}
                          label="Broken"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                }
                secondary={
                  <Tooltip
                    title={<pre style={{ margin: 0 }}>{summary.full}</pre>}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {set.description ? `${set.description} — ` : ""}
                      {summary.short}
                    </Typography>
                  </Tooltip>
                }
                actions={
                  <>
                    <IconButton
                      aria-label={`Edit ${set.name}`}
                      onClick={() => setEditing({ kind: tab, set })}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete ${set.name}`}
                      onClick={() => void handleDelete(set)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              />
            );
          })}
        </List>
      )}

      {editing && (
        <LibrarySetForm
          // Remount on target change so the form's initial state is re-seeded
          // rather than needing an effect to sync it.
          key={editing.set?.id ?? `new-${editing.kind}`}
          open
          kind={editing.kind}
          existing={editing.set}
          onClose={() => setEditing(undefined)}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};
